// KPI Builder — lets the admin edit the generated query/config in place, without leaving the builder.
// Valid edits are written into the same form fields the generator uses; changing an answer regenerates and replaces them.
import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { BuilderEdits, NO_BUILDER_EDITS, builderAnswersState, builderEditsState, builderOutputState, currentVisualizationState } from 'states';
import { splitConfig } from 'helpers/visualizationHelpers';
import type { FormBridge } from 'helpers/kpiBuilder/formBridge';
import type { BuilderOutput } from 'helpers/kpiBuilder/types';

const compact = (text: string): string => JSON.stringify(JSON.parse(text));

function writeQuery(form: FormBridge, query: string | null, output: BuilderOutput | null) {
  const next = query === null ? output?.queryString : compact(query);
  if (next !== undefined) form.set('queries', next, true);
}

function writeConfig(form: FormBridge, config: string | null, output: BuilderOutput | null) {
  const next = config === null ? (output ? JSON.stringify(output.config) : undefined) : compact(config);
  if (next === undefined) return;
  const split = splitConfig(next);
  form.set('config', split.restConfigStr, true);
  form.set('kpiFilters', split.kpiFiltersStr, true);
}

export function useBuilderEdits(form: FormBridge, builderMode: boolean) {
  const [edits, setEdits] = useRecoilState(builderEditsState);
  const answers = useRecoilValue(builderAnswersState);
  const output = useRecoilValue(builderOutputState);
  const currentVisualization = useRecoilValue(currentVisualizationState);
  const applied = useRef<BuilderEdits>(NO_BUILDER_EDITS);
  // The latest of these, read inside the effect, so it runs only when an edit changes.
  const latest = useRef({ form, output, builderMode });
  latest.current = { form, output, builderMode };

  useEffect(() => {
    const { form: f, output: o, builderMode: mode } = latest.current;
    if (!mode) return;
    if (edits.query !== applied.current.query) writeQuery(f, edits.query, o);
    if (edits.config !== applied.current.config) writeConfig(f, edits.config, o);
    applied.current = edits;
  }, [edits]);

  // A different answer or a different KPI: the generated version wins again.
  useEffect(() => {
    applied.current = NO_BUILDER_EDITS;
    setEdits(NO_BUILDER_EDITS);
  }, [answers, currentVisualization, setEdits]);

  return edits;
}
